import { FC } from 'react';
import { Checkbox } from '@carbon/react';
import { FormattedCount } from 'components/formatted-count';
import styles from './layout-settings.module.scss';

export interface LayoutSettingsItem {
  /** The card's section id, which is what the visibility record is keyed on. */
  id: string;
  label: string;
}

export interface LayoutSettingsGroup {
  /**
   * The group's heading. A named group also gets a Show all / Hide all master, as mock
   * v13's rail does. Omit it for a single ungrouped list, which then gets no master.
   */
  legend?: string;
  items: LayoutSettingsItem[];
}

interface Props {
  groups: LayoutSettingsGroup[];
  /**
   * Which cards are showing. Sparse and default-on: an id absent from the record reads
   * as visible, so a new card needs no entry and the record only holds what the reader
   * has turned off.
   */
  visible: Record<string, boolean | undefined>;
  onToggle: (id: string, visible: boolean) => void;
  /** Shows or hides a whole group, from its master checkbox. */
  onToggleGroup?: (ids: string[], visible: boolean) => void;
  /**
   * Prefixes the checkbox element ids. Required rather than defaulted so two hosts on
   * one page can never mint the same id — a duplicate id would point a label at the
   * wrong checkbox.
   */
  idPrefix: string;
}

/**
 * Whether a card is showing, given the sparse default-on record.
 * @param {object} visible The record of what has been turned off.
 * @param {string} id The card's section id.
 * @return {boolean} True unless it has been turned off.
 */
export const isCardVisible = (
  visible: Record<string, boolean | undefined>,
  id: string
): boolean => visible[id] ?? true;

/**
 * How many of a set of cards are showing.
 * @param {object} visible The record of what has been turned off.
 * @param {LayoutSettingsItem[]} items The cards to count.
 * @return {number} How many are showing.
 */
export const countVisible = (
  visible: Record<string, boolean | undefined>,
  items: LayoutSettingsItem[]
): number => items.filter((item) => isCardVisible(visible, item.id)).length;

/**
 * The "Layout settings" checkboxes — one per card, showing and hiding it.
 *
 * Shared by the Well Workspace's rail and the Well page's, which offer the same control
 * over different card sets. Only the groups are here; each host wraps them in its own
 * accordion, since the two sit in differently shaped rails.
 * @param {Props} props The groups, which cards are showing, and the toggles.
 * @return {JSX.Element} The checkbox groups.
 */
export const LayoutSettings: FC<Props> = ({
  groups,
  visible,
  onToggle,
  onToggleGroup,
  idPrefix,
}) => (
  <>
    {groups.map((group, index) => {
      const showing = countVisible(visible, group.items);
      const all = showing === group.items.length;
      // Carbon renders a dash for an indeterminate checkbox, which is what a partly
      // shown group is — neither "show all" nor "hide all" describes it.
      const some = showing > 0 && !all;
      const ids = group.items.map((item) => item.id);

      return (
        <fieldset
          className={styles.group}
          key={group.legend ?? `group-${index}`}
        >
          {/* The group always has a name for a screen reader. It is only shown when
              the panel holds more than one group; a single group's name would just
              repeat the accordion heading above it. */}
          <legend
            className={group.legend ? styles.legend : styles.legendHidden}
          >
            {group.legend ?? 'Layout settings'}
          </legend>

          {group.legend && onToggleGroup && (
            <>
              <Checkbox
                id={`${idPrefix}all-${index}`}
                labelText={all ? 'Hide all' : 'Show all'}
                checked={all}
                indeterminate={some}
                onChange={(
                  _event: unknown,
                  { checked }: { checked: boolean }
                ) => onToggleGroup(ids, checked)}
              />
              <div className={styles.divider} />
            </>
          )}

          {group.items.map((item) => (
            <Checkbox
              key={item.id}
              id={`${idPrefix}${item.id}`}
              labelText={item.label}
              checked={isCardVisible(visible, item.id)}
              onChange={(_event: unknown, { checked }: { checked: boolean }) =>
                onToggle(item.id, checked)
              }
            />
          ))}
        </fieldset>
      );
    })}
  </>
);

/**
 * A rail section's heading, with the count of what it currently holds.
 * @param {object} props The component's props.
 * @param {string} props.label The heading.
 * @param {number} [props.count] How many items the section holds, when it has a count.
 * @return {JSX.Element} The heading.
 */
export const RailSectionTitle: FC<{ label: string; count?: number }> = ({
  label,
  count,
}) => (
  <span className={styles.sectionTitle}>
    <span>{label}</span>
    {count !== undefined && (
      <span className={styles.count}>
        (<FormattedCount value={count} />)
      </span>
    )}
  </span>
);
